import { useCallback, useState } from 'react'

import { useAuth } from 'contexts/AuthContext'
import { createConversationFollowUpUpload } from 'modules/files/api'
import { usePendingUploadWorkflow, usePrepareUploadMutation } from 'modules/files/mutations'
import { usePendingUploadStore } from 'modules/files/store/usePendingUploadStore'
import { webLogger } from 'utils/logger'
import { PERCENT_SCALE } from 'utils/units'
import { getVideoDuration } from 'utils/videoUtils'

import type { PendingUploadItemInput } from 'modules/files/store/usePendingUploadStore'

type UseUploadPreparationOptions = {
  onUploadComplete: (conversationId: string) => void
  onUploadStart?: () => void
  onUploadError?: (error: string) => void
}

const MAXIMUM_IN_PROGRESS_PERCENTAGE = PERCENT_SCALE - 1

const buildUploadItem = async (
  file: File,
  context: { fileId: string; uploadUrl: string }
): Promise<PendingUploadItemInput> => {
  return {
    file,
    fileId: context.fileId,
    uploadUrl: context.uploadUrl,
  }
}

export const useUploadPreparation = (options: UseUploadPreparationOptions) => {
  const { onUploadComplete, onUploadError, onUploadStart } = options
  const { user } = useAuth()
  const { mutateAsync: prepareUpload, isPending: isPreparing } = usePrepareUploadMutation()
  const { failUpload, finishUpload, setPendingUploads, setUploadProgress, startUpload } = usePendingUploadStore()
  const { startPendingUpload } = usePendingUploadWorkflow()
  const [error, setError] = useState<string | undefined>()
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])

  const startMultiFileUploads = useCallback(
    async (conversationId: string, items: PendingUploadItemInput[]) => {
      startUpload()
      const total = items.length
      let completedWeight = 0

      try {
        for (const [index, item] of Array.from(items.entries())) {
          await startPendingUpload(
            {
              conversationId,
              file: item.file,
              fileId: item.fileId,
              uploadUrl: item.uploadUrl,
            },
            {
              onProgress: progress => {
                const overall = Math.round(
                  ((completedWeight + progress.percentage / PERCENT_SCALE) / total) * PERCENT_SCALE
                )
                setUploadProgress({ percentage: Math.min(overall, MAXIMUM_IN_PROGRESS_PERCENTAGE), phase: 'uploading' })
              },
            }
          )
          completedWeight = index + 1
          setUploadProgress({
            percentage: Math.round((completedWeight / total) * PERCENT_SCALE),
            phase: 'uploading',
          })
        }
        finishUpload()
      } catch (error_) {
        if (error_ instanceof DOMException && error_.name === 'AbortError') {
          return
        }
        const message = error_ instanceof Error ? error_.message : 'Upload failed'
        failUpload(message)
      }
    },
    [failUpload, finishUpload, setUploadProgress, startPendingUpload, startUpload]
  )

  const submitVideoFiles = useCallback(
    async (files: File[]) => {
      if (!user) {
        const authError = 'Please sign in to upload files'
        setError(authError)
        onUploadError?.(authError)
        return
      }

      if (files.length === 0) {
        return
      }

      setSelectedFiles(files)
      setError(undefined)
      onUploadStart?.()

      try {
        const [primaryFile, ...additionalFiles] = files
        const primaryDuration = await getVideoDuration(primaryFile)
        const primaryUpload = await prepareUpload({
          duration: primaryDuration,
          filename: primaryFile.name,
          mimeType: primaryFile.type || 'video/mp4',
          sizeBytes: primaryFile.size,
        })

        const pendingItems: PendingUploadItemInput[] = [
          await buildUploadItem(primaryFile, {
            fileId: primaryUpload.fileId,
            uploadUrl: primaryUpload.uploadUrl,
          }),
        ]

        for (const file of additionalFiles) {
          const duration = await getVideoDuration(file)
          const followUp = await createConversationFollowUpUpload(primaryUpload.conversationId, {
            duration: typeof duration === 'number' && duration > 0 ? duration : undefined,
            filename: file.name,
            mimeType: file.type || 'video/mp4',
            sizeBytes: file.size,
          })
          pendingItems.push(await buildUploadItem(file, followUp))
        }

        webLogger.info('Created conversation upload context', {
          conversationId: primaryUpload.conversationId,
          videoCount: pendingItems.length,
          hasUploadUrl: Boolean(primaryUpload.uploadUrl),
        })

        setPendingUploads(primaryUpload.conversationId, pendingItems)
        void startMultiFileUploads(primaryUpload.conversationId, pendingItems)
        onUploadComplete(primaryUpload.conversationId)
      } catch (error_) {
        const errorMessage = error_ instanceof Error ? error_.message : 'Failed to prepare upload. Please try again.'
        webLogger.error('Failed to prepare upload', { error: error_ })
        setError(errorMessage)
        onUploadError?.(errorMessage)
      }
    },
    [onUploadComplete, onUploadError, onUploadStart, prepareUpload, setPendingUploads, startMultiFileUploads, user]
  )

  const submitVideoFile = useCallback(
    async (file: File) => {
      await submitVideoFiles([file])
    },
    [submitVideoFiles]
  )

  const handleFilesDropped = useCallback(
    async (acceptedFiles: File[]) => {
      await submitVideoFiles(acceptedFiles)
    },
    [submitVideoFiles]
  )

  return {
    error,
    handleFilesDropped,
    isPreparing,
    selectedFile: selectedFiles[0],
    selectedFiles,
    submitVideoFile,
    submitVideoFiles,
  }
}
